import React, { useState, useMemo } from 'react'
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Chip,
    Tooltip,
    CircularProgress,
} from '@mui/material'
import SvgIcon from '@mui/material/SvgIcon'
import { ReactComponent as PushPinSvg } from '../../../assets/images/PushPin.svg'
import { useAdminFeatureAdoptionQuery, FeatureAdoptionOrg } from '../../../shared/queries/admin'

const FeatureAdoptionTab: React.FC = () => {
    const { data, isLoading } = useAdminFeatureAdoptionQuery()
    const [sortField, setSortField] = useState<string>('events')
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

    const adoption = data?.data?.adoption || []

    const sortedAdoption = useMemo(() => {
        return [...adoption].sort((a, b) => {
            // First sort by pinned (favorites first)
            if (a.isFavorite && !b.isFavorite) return -1
            if (!a.isFavorite && b.isFavorite) return 1

            // Then by the selected sort field
            let aVal: number | string = ''
            let bVal: number | string = ''

            switch (sortField) {
                case 'name':
                    aVal = a.name.toLowerCase()
                    bVal = b.name.toLowerCase()
                    break
                case 'users':
                    aVal = a.usage.users
                    bVal = b.usage.users
                    break
                case 'roadmaps':
                    aVal = a.usage.roadmaps
                    bVal = b.usage.roadmaps
                    break
                case 'vms':
                    aVal = a.usage.valueMessages
                    bVal = b.usage.valueMessages
                    break
                case 'evidence':
                    aVal = a.usage.evidence
                    bVal = b.usage.evidence
                    break
                case 'activities':
                    aVal = a.usage.activities
                    bVal = b.usage.activities
                    break
                case 'events':
                    aVal = a.activity?.totalEvents ?? 0
                    bVal = b.activity?.totalEvents ?? 0
                    break
                default:
                    // Default sort by total events
                    aVal = a.activity?.totalEvents ?? 0
                    bVal = b.activity?.totalEvents ?? 0
            }

            if (typeof aVal === 'string') {
                return sortDir === 'asc' ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal)
            }
            return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number)
        })
    }, [adoption, sortField, sortDir])

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDir('asc')
        }
    }

    const formatLastActivity = (dateString: string | null) => {
        if (!dateString) return 'Never'
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return 'Never'
        const now = new Date()
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
        if (diffDays === 0) return 'Today'
        if (diffDays === 1) return 'Yesterday'
        if (diffDays < 7) return `${diffDays}d ago`
        if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
        return `${Math.floor(diffDays / 30)}mo ago`
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box>
            <Typography variant="body2" color="figma.grayscale.textHier2" sx={{ mb: 2 }}>
                {adoption.length} organizations
            </Typography>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'name'}
                                    direction={sortField === 'name' ? sortDir : 'asc'}
                                    onClick={() => handleSort('name')}
                                >
                                    Organization
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="MAIA AI Feature">
                                    <span>MAIA</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="PICO AI Feature">
                                    <span>PICO</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="Restricts roadmap access to specific users">
                                    <span>Roadmap Restriction</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'users'}
                                    direction={sortField === 'users' ? sortDir : 'asc'}
                                    onClick={() => handleSort('users')}
                                >
                                    Users
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'roadmaps'}
                                    direction={sortField === 'roadmaps' ? sortDir : 'asc'}
                                    onClick={() => handleSort('roadmaps')}
                                >
                                    Roadmaps
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'vms'}
                                    direction={sortField === 'vms' ? sortDir : 'asc'}
                                    onClick={() => handleSort('vms')}
                                >
                                    VMs
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'evidence'}
                                    direction={sortField === 'evidence' ? sortDir : 'asc'}
                                    onClick={() => handleSort('evidence')}
                                >
                                    Evidence
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'activities'}
                                    direction={sortField === 'activities' ? sortDir : 'asc'}
                                    onClick={() => handleSort('activities')}
                                >
                                    Activities
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <Tooltip title="Events since June 4, 2025">
                                    <TableSortLabel
                                        active={sortField === 'events'}
                                        direction={sortField === 'events' ? sortDir : 'asc'}
                                        onClick={() => handleSort('events')}
                                    >
                                        Events
                                    </TableSortLabel>
                                </Tooltip>
                            </TableCell>
                            <TableCell>Last Active</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedAdoption.map((org: FeatureAdoptionOrg) => (
                            <TableRow key={org._id} hover>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {org.isFavorite && (
                                            <SvgIcon
                                                component={PushPinSvg}
                                                inheritViewBox
                                                sx={{ fontSize: 14, color: 'figma.primary' }}
                                            />
                                        )}
                                        <Typography variant="body2" fontWeight={500}>
                                            {org.name}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            bgcolor: org.features.maia ? 'figma.primary' : 'transparent',
                                            border: '2px solid',
                                            borderColor: org.features.maia ? 'figma.primary' : 'figma.grayscale.stroke',
                                            mx: 'auto',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            bgcolor: org.features.pico ? 'figma.primary' : 'transparent',
                                            border: '2px solid',
                                            borderColor: org.features.pico ? 'figma.primary' : 'figma.grayscale.stroke',
                                            mx: 'auto',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            bgcolor: org.features.allowedUsers ? 'figma.primary' : 'transparent',
                                            border: '2px solid',
                                            borderColor: org.features.allowedUsers
                                                ? 'figma.primary'
                                                : 'figma.grayscale.stroke',
                                            mx: 'auto',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">{org.usage.users}</TableCell>
                                <TableCell align="right">{org.usage.roadmaps}</TableCell>
                                <TableCell align="right">{org.usage.valueMessages}</TableCell>
                                <TableCell align="right">{org.usage.evidence}</TableCell>
                                <TableCell align="right">{org.usage.activities}</TableCell>
                                <TableCell align="right">
                                    <Chip
                                        label={(org.activity?.totalEvents ?? 0).toLocaleString()}
                                        size="small"
                                        color={
                                            (org.activity?.totalEvents ?? 0) > 100
                                                ? 'success'
                                                : (org.activity?.totalEvents ?? 0) > 10
                                                  ? 'default'
                                                  : 'error'
                                        }
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        title={
                                            org.activity?.lastActivity
                                                ? new Date(org.activity.lastActivity).toLocaleString(undefined, {
                                                      timeZoneName: 'short',
                                                  })
                                                : ''
                                        }
                                    >
                                        <Typography
                                            variant="caption"
                                            color={org.activity?.lastActivity ? 'text.secondary' : 'error'}
                                        >
                                            {formatLastActivity(org.activity?.lastActivity)}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default FeatureAdoptionTab
